import { cloneElement, ReactElement } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { StaffRole } from './staffRole';
import { useCanAccess } from './useCanAccess';

type RequireTierPolicy = 'disable' | 'hide';

interface RequireTierProps {
  readonly requiredTier: StaffRole;
  readonly policy: RequireTierPolicy;
  readonly children: ReactElement;
  readonly fullWidth?: boolean;
}

/**
 * Advisory UX gate (COR-545). When the signed-in staff member's tier meets
 * `requiredTier`, the child renders unchanged. Below the bar:
 *   - `policy="hide"` renders nothing. Per the COR-545 tier map this is reserved
 *     for the specific destructive tools that must not even be discoverable below
 *     superadmin: both anonymize flows and the api-token mint.
 *   - `policy="disable"` clones the child with `disabled` and wraps it in a
 *     `d-inline-block` span (pointer-events wrapper) inside an OverlayTrigger so a
 *     "requires <tier>" tooltip still surfaces on hover — a disabled control alone
 *     swallows the hover event. This is the default for everything else, INCLUDING
 *     admin routine-but-irreversible ops such as cache-clear and bulk-delete: they
 *     stay discoverable (disabled + tooltip) so support knows the tool exists and
 *     which tier unlocks it.
 * So the hide-vs-disable choice is rule-derived, not ad hoc: hide only the
 * anonymize/api-token-mint tools; disable everything else.
 * The backend remains the authorization boundary; this only mirrors it for UX.
 */
export function RequireTier(props: RequireTierProps): ReactElement | null {
  const { requiredTier, policy, children, fullWidth } = props;
  const canAccess = useCanAccess(requiredTier);

  if (canAccess) {
    return children;
  }

  if (policy === 'hide') {
    return null;
  }

  const disabledChild = cloneElement(children, { disabled: true });
  const wrapperClassName = fullWidth ? 'd-inline-block w-100' : 'd-inline-block';

  return (
    <OverlayTrigger
      placement="top"
      overlay={<Tooltip id={`require-tier-${requiredTier}`}>requires {requiredTier}</Tooltip>}
    >
      <span className={wrapperClassName}>{disabledChild}</span>
    </OverlayTrigger>
  );
}
